import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { keys, useTemplates } from "../../hooks/queries";
import { api } from "../../lib/api";
import { Pill, SectionHeader } from "../ui/Badges";

/** Display order of the automation rules. */
export const RULE_CODES = [
  "TPL_WELCOME_V1",
  "TPL_EXPIRY_WARNING_V2",
  "TPL_OVERDUE_V1",
  "TPL_BLOCK_ENDING_V1",
  "TPL_EXTENDED_V1",
  "TPL_EXTEND_REQUEST_V1",
  "TPL_GATE_PASS_V1",
  "TPL_BIRTHDAY_FOLLOWUP_V1",
] as const;

export function AutomationRules() {
  const { t } = useTranslation();
  const { isManager } = useAuth();
  const { toast, toastError } = useToast();
  const qc = useQueryClient();
  const { data } = useTemplates();

  const rules = useMemo(
    () => RULE_CODES.map((code) => ({ code, enabled: (data ?? []).filter((x) => x.code === code).every((x) => x.enabled) && (data ?? []).some((x) => x.code === code) })),
    [data],
  );
  const allOn = rules.every((r) => r.enabled);

  async function toggle(code: string, enabled: boolean) {
    try {
      await api.patch(`/whatsapp/rules/${code}`, { enabled });
      await qc.invalidateQueries({ queryKey: keys.templates });
      toast(t(enabled ? "whatsapp.ruleOn" : "whatsapp.ruleOff"), "info");
    } catch (e) {
      toastError(e);
    }
  }

  return (
    <section className="card space-y-3 p-4" aria-label={t("whatsapp.rules")}>
      <SectionHeader title={`🤖 ${t("whatsapp.rules")}`} right={<Pill tone={allOn ? "green" : "amber"}>{allOn ? t("whatsapp.allRulesActive") : t("whatsapp.someRulesOff")}</Pill>} />
      <div className="space-y-2 text-xs font-bold">
        {rules.map((r) => (
          <label key={r.code} className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 p-2">
            <span>
              <span className="block text-cosmic-navy">{t(`rules.${r.code}.title`)}</span>
              <span className="block text-[10px] font-normal text-gray-500">{t(`rules.${r.code}.desc`)}</span>
            </span>
            <input type="checkbox" role="switch" className="rounded text-cosmic-purple focus:ring-0" checked={r.enabled} disabled={!isManager} onChange={(e) => toggle(r.code, e.target.checked)} aria-label={t(`rules.${r.code}.title`)} />
          </label>
        ))}
      </div>
      {!isManager && <p className="text-[10px] text-gray-400">{t("whatsapp.managerOnly")}</p>}
    </section>
  );
}
